import {
  compareReplayHistories,
  compareReplaySnapshots,
} from '../engine/replay/ReplayDivergenceDetector.js';
import { scenarios } from './scenarios.js';

export function installTestApi({
  bus,
  clock,
  random,
  featureFlags,
  input,
  inputReplay,
  replayDivergence,
  entityWorld,
  traversalWorld,
  snakeState,
  wildlifeSystem,
  huntingSystem,
  storySystem,
  saveSystem,
  simulationSnapshots,
  advanceSimulation,
  resetSession,
  getInputSnapshot,
  getSnapshot,
}) {
  const isPlayerSnakeType = (value) => (
    typeof value === 'string' && value.trim() === 'player-snake'
  );
  const isWildlifeType = (value) => (
    typeof value === 'string' && value.trim() === 'wildlife'
  );

  const emitEntityWorldChange = (operation, entity = null) => {
    const status = entityWorld.getStatus();
    bus.emit('entity-world:changed', { operation, entity, status });
    return status;
  };

  const emitTraversalWorldChange = (operation, value = null) => {
    const status = traversalWorld.getStatus();
    bus.emit('traversal-world:changed', { operation, value, status });
    return status;
  };

  const emitSnakeStateChange = (operation, state = snakeState.snapshot()) => {
    const status = snakeState.getStatus();
    bus.emit('snake-state:changed', { operation, state, status });
    emitTraversalWorldChange(`snake:${operation}`, state
      ? traversalWorld.getActorState(state.entityId)
      : null);
    emitEntityWorldChange(`snake:${operation}`, state ? entityWorld.get(state.entityId) : null);
    return status;
  };

  const emitWildlifeStateChange = (operation, record = null) => {
    const status = wildlifeSystem.getStatus();
    bus.emit('wildlife-state:changed', { operation, record, status });
    emitTraversalWorldChange(
      `wildlife:${operation}`,
      record ? traversalWorld.getActorState(record.entityId) : null,
    );
    emitEntityWorldChange(
      `wildlife:${operation}`,
      record ? entityWorld.get(record.entityId) : null,
    );
    return status;
  };

  const emitHuntingStateChange = (operation, event = null) => {
    const status = huntingSystem.getStatus();
    bus.emit('hunting-state:changed', { operation, event, status });
    emitTraversalWorldChange(`hunting:${operation}`);
    return status;
  };

  const emitStoryStateChange = (operation, value = null) => {
    const status = storySystem.getStatus();
    bus.emit('story-state:changed', { operation, value, status });
    return status;
  };

  const emitSaveStateChange = (operation, value = null) => {
    const status = saveSystem.getStatus();
    bus.emit('save-state:changed', { operation, value, status });
    return status;
  };

  const assertGenericEntityMutationAllowed = (id) => {
    if (snakeState.ownsEntity(id)) {
      throw new RangeError('Use the snake-state API to mutate the active player snake');
    }
    if (wildlifeSystem.ownsEntity(id)) {
      throw new RangeError('Use the wildlife API to mutate tracked wildlife');
    }
  };

  const assertWorldReplacementAllowed = (snapshot = undefined) => {
    if (snakeState.getStatus().active) {
      throw new RangeError('Reset the active player snake before replacing the entity world');
    }
    if (wildlifeSystem.getStatus().count > 0) {
      throw new RangeError('Reset tracked wildlife before replacing the entity world');
    }
    if (huntingSystem.getStatus().processedRequestCount > 0) {
      throw new RangeError('Reset hunting state before replacing the entity world');
    }
    if (traversalWorld.getStatus().actorCount > 0) {
      throw new RangeError('Reset traversal actor state before replacing the entity world');
    }
    if (snapshot === undefined) return;
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    if (Array.isArray(parsed?.entities)
      && parsed.entities.some((entity) => isPlayerSnakeType(entity?.type))) {
      throw new RangeError('Use restorePlayerSnakeSnapshot() to restore a player snake');
    }
    if (Array.isArray(parsed?.entities)
      && parsed.entities.some((entity) => isWildlifeType(entity?.type))) {
      throw new RangeError('Use restoreWildlifeSnapshot() to restore wildlife');
    }
  };

  const assertTraversalReplacementAllowed = () => {
    if (snakeState.getStatus().active) {
      throw new RangeError('Reset the active player snake before replacing traversal state');
    }
    if (wildlifeSystem.getStatus().count > 0) {
      throw new RangeError('Reset tracked wildlife before replacing traversal state');
    }
  };

  const currentInteractionTiming = (options = {}) => ({
    tick: options.tick ?? simulationSnapshots.getStatus().nextTick,
    simulationTime: options.simulationTime ?? clock.simulationTime,
  });

  const assertStorySnapshotTiming = (snapshot) => {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    const nextTick = simulationSnapshots.getStatus().nextTick;
    if (parsed?.lastSteppedTick !== null
      && parsed?.lastSteppedTick !== undefined
      && parsed.lastSteppedTick >= nextTick) {
      throw new RangeError('Story snapshot must precede the next simulation tick');
    }
  };

  const api = Object.freeze({
    version: '32.0.0-dev',
    inputVersion: 1,
    inputRecordingVersion: 1,
    simulationStepVersion: 1,
    randomVersion: 1,
    simulationSessionVersion: 1,
    replayDivergenceVersion: 1,
    entityWorldVersion: 1,
    traversalWorldVersion: 1,
    snakeStateVersion: 1,
    wildlifeSystemVersion: 2,
    huntingSystemVersion: 1,
    storySystemVersion: 1,
    saveSystemVersion: 1,
    channel: __BUILD_CHANNEL__,
    commit: __BUILD_COMMIT__,
    listScenarios: () => scenarios.map((scenario) => ({ ...scenario })),
    startScenario: (id) => {
      const scenario = scenarios.find((entry) => entry.id === id);
      if (!scenario) throw new Error(`Unknown scenario: ${id}`);
      bus.emit('scenario:start', scenario);
      return { ok: true, scenario: { ...scenario } };
    },
    setTime: (timeOfDay) => {
      bus.emit('world:set-time', { timeOfDay });
      return { ok: true, timeOfDay };
    },
    setWeather: (weather) => {
      bus.emit('world:set-weather', { weather });
      return { ok: true, weather };
    },
    setTimeScale: (scale) => {
      clock.setTimeScale(scale);
      return clock.snapshot();
    },
    pause: () => {
      clock.setPaused(true);
      return clock.snapshot();
    },
    resume: () => {
      clock.setPaused(false);
      return clock.snapshot();
    },
    advanceSimulation,
    resetSimulationSession: (options = {}) => ({
      ok: true,
      session: resetSession(options),
    }),
    setFeatureFlag: (name, enabled) => {
      featureFlags.set(name, enabled);
      return featureFlags.snapshot();
    },
    getRandomState: () => random.snapshot(),
    setRandomSeed: (seed) => ({ ok: true, random: random.setSeed(seed) }),
    resetRandom: () => ({ ok: true, random: random.reset() }),
    restoreRandomState: (snapshot) => ({ ok: true, random: random.restore(snapshot) }),
    nextRandomFloat: () => ({ value: random.nextFloat(), random: random.snapshot() }),
    nextRandomInt: (min, maxExclusive) => ({
      value: random.nextInt(min, maxExclusive),
      random: random.snapshot(),
    }),
    getInputSnapshot,
    setVirtualInput: (state) => {
      const virtual = input.setVirtualInput(state);
      return { ok: true, virtual, input: getInputSnapshot() };
    },
    clearVirtualInput: () => {
      input.clearVirtualInput();
      return { ok: true, input: getInputSnapshot() };
    },
    startInputRecording: (options = {}) => ({
      ok: true,
      status: inputReplay.startRecording({
        stepSeconds: options.stepSeconds ?? clock.stepSeconds,
        metadata: {
          channel: __BUILD_CHANNEL__,
          commit: __BUILD_COMMIT__,
          ...(options.metadata || {}),
        },
      }),
    }),
    stopInputRecording: () => {
      const recording = inputReplay.stopRecording();
      return { ok: Boolean(recording), recording, status: inputReplay.getStatus() };
    },
    getInputRecording: () => inputReplay.getRecording(),
    exportInputRecording: () => inputReplay.exportRecording(),
    importInputRecording: (recording) => ({
      ok: true,
      status: inputReplay.importRecording(recording),
    }),
    startInputReplay: (recording = undefined) => ({
      ok: true,
      status: inputReplay.startReplay(recording),
    }),
    pauseInputReplay: () => ({ ok: true, status: inputReplay.pauseReplay() }),
    resumeInputReplay: () => ({ ok: true, status: inputReplay.resumeReplay() }),
    seekInputReplay: (frame) => ({ ok: true, status: inputReplay.seek(frame) }),
    resetInputReplay: () => ({ ok: true, status: inputReplay.resetReplay() }),
    stopInputReplay: () => ({ ok: true, status: inputReplay.stopReplay() }),
    getInputReplayStatus: () => inputReplay.getStatus(),
    startReplayVerification: (expectedSnapshots, options = {}) => ({
      ok: true,
      status: replayDivergence.start(expectedSnapshots, options),
    }),
    finishReplayVerification: () => ({ ok: true, status: replayDivergence.finish() }),
    resetReplayVerification: () => ({ ok: true, status: replayDivergence.reset() }),
    getReplayVerificationStatus: () => replayDivergence.getStatus(),
    compareReplaySnapshots,
    compareReplayHistories,
    exportReplayDivergenceDiagnostic: (metadata = {}) => replayDivergence.exportDiagnostic({
      channel: __BUILD_CHANNEL__,
      commit: __BUILD_COMMIT__,
      ...metadata,
    }),
    spawnEntity: (definition) => {
      if (isPlayerSnakeType(definition?.type)) {
        throw new RangeError('Use spawnPlayerSnake() to create the player snake');
      }
      if (isWildlifeType(definition?.type)) {
        throw new RangeError('Use spawnWildlife() to create wildlife');
      }
      const entity = entityWorld.spawn(definition);
      bus.emit('entity:spawn', entity);
      return { ok: true, entity, status: emitEntityWorldChange('spawn', entity) };
    },
    updateEntity: (id, patch) => {
      assertGenericEntityMutationAllowed(id);
      const entity = entityWorld.update(id, patch);
      bus.emit('entity:update', entity);
      return { ok: true, entity, status: emitEntityWorldChange('update', entity) };
    },
    removeEntity: (id) => {
      assertGenericEntityMutationAllowed(id);
      const entity = entityWorld.remove(id);
      if (entity) bus.emit('entity:remove', entity);
      return { ok: Boolean(entity), entity, status: emitEntityWorldChange('remove', entity) };
    },
    getEntity: (id) => entityWorld.get(id),
    listEntities: (options = {}) => entityWorld.list(options),
    queryEntities: (options = {}) => entityWorld.query(options),
    getEntityWorldStatus: () => entityWorld.getStatus(),
    captureEntityWorldSnapshot: () => entityWorld.snapshot(),
    restoreEntityWorldSnapshot: (snapshot) => {
      assertWorldReplacementAllowed(snapshot);
      const world = entityWorld.restore(snapshot);
      bus.emit('entity-world:restore', world);
      return { ok: true, world, status: emitEntityWorldChange('restore') };
    },
    resetEntityWorld: (options = {}) => {
      assertWorldReplacementAllowed();
      const status = entityWorld.reset(options);
      bus.emit('entity-world:reset', status);
      emitEntityWorldChange('reset');
      return { ok: true, status };
    },
    addTraversalVolume: (definition) => {
      const volume = traversalWorld.addVolume(definition);
      bus.emit('traversal:volume-add', volume);
      return {
        ok: true,
        volume,
        status: emitTraversalWorldChange('volume-add', volume),
      };
    },
    removeTraversalVolume: (id) => {
      const volume = traversalWorld.removeVolume(id);
      if (volume) bus.emit('traversal:volume-remove', volume);
      return {
        ok: Boolean(volume),
        volume,
        status: emitTraversalWorldChange('volume-remove', volume),
      };
    },
    getTraversalVolume: (id) => traversalWorld.getVolume(id),
    listTraversalVolumes: (options = {}) => traversalWorld.listVolumes(options),
    queryTraversal: (options) => traversalWorld.queryPoint(options),
    previewTraversalMovement: (options) => traversalWorld.resolveMovement(options),
    getTraversalActorState: (id) => traversalWorld.getActorState(id),
    listTraversalActorStates: (options = {}) => traversalWorld.listActorStates(options),
    getTraversalStatus: () => traversalWorld.getStatus(),
    getTraversalDiagnostics: () => traversalWorld.getDiagnostics(),
    captureTraversalSnapshot: () => traversalWorld.snapshot(),
    exportTraversalSnapshot: () => traversalWorld.exportSnapshot(),
    restoreTraversalSnapshot: (snapshot) => {
      assertTraversalReplacementAllowed();
      const state = traversalWorld.restore(snapshot);
      bus.emit('traversal:restore', state);
      return { ok: true, state, status: emitTraversalWorldChange('restore', state) };
    },
    resetTraversalWorld: () => {
      assertTraversalReplacementAllowed();
      const status = traversalWorld.reset();
      bus.emit('traversal:reset', status);
      emitTraversalWorldChange('reset', status);
      return { ok: true, status };
    },
    spawnPlayerSnake: (options = {}) => {
      const state = snakeState.spawn(options);
      bus.emit('snake:spawn', state);
      return { ok: true, state, status: emitSnakeStateChange('spawn', state) };
    },
    getPlayerSnakeState: () => snakeState.snapshot(),
    getPlayerSnakeStatus: () => snakeState.getStatus(),
    damagePlayerSnake: (amount) => {
      const state = snakeState.damage(amount);
      bus.emit('snake:damage', state);
      return { ok: true, state, status: emitSnakeStateChange('damage', state) };
    },
    feedPlayerSnake: (amount) => {
      const state = snakeState.feed(amount);
      bus.emit('snake:feed', state);
      return { ok: true, state, status: emitSnakeStateChange('feed', state) };
    },
    growPlayerSnake: (options = {}) => {
      const state = snakeState.grow(options);
      bus.emit('snake:grow', state);
      return { ok: true, state, status: emitSnakeStateChange('grow', state) };
    },
    teleportPlayerSnake: (options) => {
      const state = snakeState.teleport(options);
      bus.emit('snake:teleport', state);
      return { ok: true, state, status: emitSnakeStateChange('teleport', state) };
    },
    capturePlayerSnakeSnapshot: () => snakeState.snapshot(),
    exportPlayerSnakeSnapshot: () => snakeState.exportSnapshot(),
    restorePlayerSnakeSnapshot: (snapshot) => {
      const state = snakeState.restore(snapshot);
      bus.emit('snake:restore', state);
      return { ok: true, state, status: emitSnakeStateChange('restore', state) };
    },
    resetPlayerSnake: () => {
      const status = snakeState.reset();
      bus.emit('snake:reset', status);
      emitSnakeStateChange('reset', null);
      return { ok: true, status };
    },
    listWildlifeSpecies: () => wildlifeSystem.listSpecies(),
    spawnWildlife: (speciesId, options = {}) => {
      const record = wildlifeSystem.spawn(speciesId, options);
      bus.emit('wildlife:spawn', record);
      return { ok: true, record, status: emitWildlifeStateChange('spawn', record) };
    },
    getWildlife: (id) => wildlifeSystem.get(id),
    listWildlife: (options = {}) => wildlifeSystem.list(options),
    removeWildlife: (id) => {
      const record = wildlifeSystem.remove(id);
      if (record) bus.emit('wildlife:remove', record);
      return {
        ok: Boolean(record),
        record,
        status: emitWildlifeStateChange('remove', record),
      };
    },
    getWildlifeStatus: () => wildlifeSystem.getStatus(),
    captureWildlifeSnapshot: () => wildlifeSystem.snapshot(),
    exportWildlifeSnapshot: () => wildlifeSystem.exportSnapshot(),
    restoreWildlifeSnapshot: (snapshot) => {
      const state = wildlifeSystem.restore(snapshot);
      bus.emit('wildlife:restore', state);
      return { ok: true, state, status: emitWildlifeStateChange('restore') };
    },
    resetWildlife: () => {
      const status = wildlifeSystem.reset();
      bus.emit('wildlife:reset', status);
      emitWildlifeStateChange('reset');
      return { ok: true, status };
    },
    strikeWildlife: (options = {}) => {
      const event = huntingSystem.strike({
        ...options,
        ...currentInteractionTiming(options),
      });
      bus.emit('hunting:strike', event);
      emitSnakeStateChange('hunting:strike', snakeState.snapshot());
      emitWildlifeStateChange('hunting:strike', event.targetEntityId
        ? wildlifeSystem.get(event.targetEntityId)
        : null);
      return { ok: event.ok, event, status: emitHuntingStateChange('strike', event) };
    },
    eatWildlife: (options = {}) => {
      const event = huntingSystem.eat({
        ...options,
        ...currentInteractionTiming(options),
      });
      bus.emit('hunting:eat', event);
      emitSnakeStateChange('hunting:eat', snakeState.snapshot());
      emitWildlifeStateChange('hunting:eat', event.targetEntityId
        ? wildlifeSystem.get(event.targetEntityId)
        : null);
      return { ok: event.ok, event, status: emitHuntingStateChange('eat', event) };
    },
    getHuntingStatus: () => huntingSystem.getStatus(),
    captureHuntingSnapshot: () => huntingSystem.snapshot(),
    exportHuntingSnapshot: () => huntingSystem.exportSnapshot(),
    restoreHuntingSnapshot: (snapshot) => {
      const state = huntingSystem.restore(snapshot);
      bus.emit('hunting:restore', state);
      return { ok: true, state, status: emitHuntingStateChange('restore') };
    },
    resetHunting: () => {
      const status = huntingSystem.reset();
      bus.emit('hunting:reset', status);
      emitHuntingStateChange('reset');
      return { ok: true, status };
    },
    getCampaignDefinition: () => storySystem.getDefinition(),
    listCampaignChapters: () => storySystem.getDefinition().chapterCatalog.map((chapter) => ({
      ...chapter,
    })),
    listStoryMissions: () => storySystem.listMissions(),
    getStoryMission: (id) => storySystem.getMission(id),
    startStoryCampaign: (options = {}) => {
      const state = storySystem.startCampaign(currentInteractionTiming(options));
      bus.emit('story:start', { mode: 'campaign', state });
      return { ok: true, state, status: emitStoryStateChange('start-campaign', state) };
    },
    startStorySimulation: (options = {}) => {
      const state = storySystem.startSimulation(currentInteractionTiming(options));
      bus.emit('story:start', { mode: 'simulation', state });
      return { ok: true, state, status: emitStoryStateChange('start-simulation', state) };
    },
    activateStoryMission: (id, options = {}) => {
      const state = storySystem.activateMission(id, currentInteractionTiming(options));
      bus.emit('story:mission-activate', state);
      return { ok: true, state, status: emitStoryStateChange('mission-activate', state) };
    },
    failStoryMission: (id = undefined, options = {}) => {
      const state = storySystem.failMission(id, currentInteractionTiming(options));
      bus.emit('story:mission-fail', state);
      return { ok: true, state, status: emitStoryStateChange('mission-fail', state) };
    },
    cancelStoryMission: (id = undefined, options = {}) => {
      const state = storySystem.cancelMission(id, currentInteractionTiming(options));
      bus.emit('story:mission-cancel', state);
      return { ok: true, state, status: emitStoryStateChange('mission-cancel', state) };
    },
    restartStoryMission: (id, options = {}) => {
      const state = storySystem.restartMission(id, currentInteractionTiming(options));
      bus.emit('story:mission-restart', state);
      return { ok: true, state, status: emitStoryStateChange('mission-restart', state) };
    },
    submitStoryEvent: (event, options = {}) => {
      const timing = currentInteractionTiming(options);
      const result = storySystem.submitEvent({ ...event, ...timing });
      bus.emit('story:event', result);
      return { ok: result.ok, result, status: emitStoryStateChange('event', result) };
    },
    chooseStoryOption: (choiceId, optionId, options = {}) => {
      const result = storySystem.choose(
        choiceId,
        optionId,
        currentInteractionTiming(options),
      );
      bus.emit('story:choice', result);
      return { ok: true, result, status: emitStoryStateChange('choice', result) };
    },
    getStoryStatus: () => storySystem.getStatus(),
    captureStorySnapshot: () => storySystem.snapshot(),
    exportStorySnapshot: () => storySystem.exportSnapshot(),
    restoreStorySnapshot: (snapshot) => {
      assertStorySnapshotTiming(snapshot);
      const state = storySystem.restore(snapshot);
      bus.emit('story:restore', state);
      return { ok: true, state, status: emitStoryStateChange('restore', state) };
    },
    resetStorySystem: () => {
      const status = storySystem.reset();
      bus.emit('story:reset', status);
      emitStoryStateChange('reset', status);
      return { ok: true, status };
    },
    getSaveStatus: () => saveSystem.getStatus(),
    listSaveSlots: () => saveSystem.listSlots(),
    captureSaveEnvelope: (options = {}) => saveSystem.captureEnvelope(options),
    verifySaveEnvelope: (value) => saveSystem.verifyEnvelope(value),
    saveToSlot: (slotId, options = {}) => {
      const result = saveSystem.saveSlot(slotId, options);
      bus.emit('save:write', result);
      emitSaveStateChange('save', result);
      return result;
    },
    loadFromSlot: (slotId) => {
      const result = saveSystem.loadSlot(slotId);
      bus.emit('save:load', result);
      emitSaveStateChange('load', result);
      return result;
    },
    exportSaveSlot: (slotId) => saveSystem.exportSlot(slotId),
    importSaveSlot: (slotId, value, options = {}) => {
      const result = saveSystem.importSlot(slotId, value, options);
      bus.emit('save:import', result);
      emitSaveStateChange('import', result);
      return result;
    },
    deleteSaveSlot: (slotId) => {
      const result = saveSystem.deleteSlot(slotId);
      bus.emit('save:delete', result);
      emitSaveStateChange('delete', result);
      return result;
    },
    clearSaveSlots: () => {
      const result = saveSystem.clearSlots();
      bus.emit('save:clear', result);
      emitSaveStateChange('clear', result);
      return result;
    },
    getSimulationStepSnapshot: (tick = undefined) => tick === undefined
      ? simulationSnapshots.getLatest()
      : simulationSnapshots.get(tick),
    getSimulationStepHistory: (options = {}) => simulationSnapshots.list(options),
    getSimulationStepStatus: () => simulationSnapshots.getStatus(),
    verifySimulationStepSnapshot: (snapshot) => simulationSnapshots.verify(snapshot),
    captureSnapshot: () => ({
      capturedAt: new Date().toISOString(),
      channel: __BUILD_CHANNEL__,
      commit: __BUILD_COMMIT__,
      clock: clock.snapshot(),
      random: random.snapshot(),
      features: featureFlags.snapshot(),
      input: getInputSnapshot(),
      inputReplay: inputReplay.getStatus(),
      replayDivergence: replayDivergence.getStatus(),
      entityWorld: entityWorld.snapshot(),
      traversalWorld: traversalWorld.snapshot(),
      snakeState: snakeState.snapshot(),
      wildlifeSystem: wildlifeSystem.snapshot(),
      huntingSystem: huntingSystem.snapshot(),
      storySystem: storySystem.snapshot(),
      saveSystem: saveSystem.getStatus(),
      saveSlots: saveSystem.listSlots(),
      simulationStep: simulationSnapshots.getLatest(),
      simulationSnapshots: simulationSnapshots.getStatus(),
      game: getSnapshot(),
    }),
  });

  Object.defineProperty(window, '__SERPENT_TEST__', {
    configurable: true,
    enumerable: false,
    value: api,
  });

  return api;
}
